import {
  Box,
  chakra,
  Container,
  GridItem,
  Link,
  SimpleGrid,
  Text,
  useColorModeValue,
} from '@chakra-ui/react'
import { ButtonChakra } from 'components/Button'
import Header from 'components/Header'
import RegisterForm from 'components/RegisterForm'
import RegisterInfo from 'components/RegisterInfo'
import { useRegister } from 'controllers/register.controller'
import { GetServerSideProps } from 'next'
import NextLink from 'next/link'
import { useRouter } from 'next/router'
import { AuthService } from 'services/auth.service'
import { MembershipService } from 'services/membership.service'
import { UserService } from 'services/user.service'
import { compose } from 'ssr/compose'
import {
  IMembershipInvitationProps,
  MembershipInvitationFetcher,
} from 'ssr/middlewares/fetchers/membershipInvitation.fetcher'
import { auth, AuthGuardMode } from 'ssr/middlewares/guards/auth.guard'
import { CanAccessPublicRoutes } from 'ssr/middlewares/guards/canAccessPublicRoutes.guard'
import { MembershipAcceptedRedirecter } from 'ssr/middlewares/redirecters/membershipAccepted.redirecter'
import { MembershipInvitationRedirecter } from 'ssr/middlewares/redirecters/membershipInvitation.redirecter'

function Register(props: IMembershipInvitationProps) {
  const controller = useRegister(props)

  const router = useRouter()

  const buildHref = () => {
    if (router.query.next) {
      return `/login?next=${encodeURIComponent(router.query.next)}`
    }

    if (props.membershipInvitation) {
      return `/login?hasToAcceptInvitation=true&invitationToken=${router.query.invitationToken}`
    }

    return `/login`
  }

  return (
    <Container
      maxW={[
        'container.lg',
        'container.lg',
        'container.md',
        'container.lg',
        'container.standard',
      ]}
      mt={[8, 8, 12]}
    >
      <Header title='Registro de usuario' />
      <Box mt={[8, 0]}>
        <SimpleGrid
          display={{ base: 'initial', md: 'grid' }}
          columns={{ md: 12 }}
          spacing={{ md: 6 }}
        >
          <RegisterInfo
            buildHref={buildHref}
            membershipInvitation={props.membershipInvitation}
          />
          <GridItem mt={[5, null, 0]} colSpan={{ md: 6 }}>
            <chakra.form
              shadow='lg'
              rounded='landing'
              overflow={{ sm: 'hidden' }}
              onSubmit={controller.handleSubmit(controller.onSubmit)}
            >
              <RegisterForm
                register={controller.register}
                errors={controller.errors}
                showPassword={controller.showPassword}
                setShowPassword={controller.setShowPassword}
              />
              <Box
                px={{ base: 4, sm: 6 }}
                py={3}
                bg={useColorModeValue('gray.50', 'gray.900')}
                textAlign='right'
              >
                <ButtonChakra
                  isDisabled={!controller.isValid}
                  type='submit'
                  colorScheme='brand'
                  _focus={{ shadow: '' }}
                  fontWeight='md'
                  w='100%'
                  bg='landingPrimary'
                  borderRadius={'landing'}
                  isLoading={controller.loading}
                >
                  Aceptar
                </ButtonChakra>
              </Box>
            </chakra.form>
          </GridItem>
        </SimpleGrid>
        <Text
          display={['block', 'block', 'none']}
          textAlign='center'
          fontSize='sm'
          my='4'
        >
          ¿Ya tienes una cuenta?{' '}
          <Link href={buildHref()} as={NextLink} fontWeight='bold'>
            Inicia sesión
          </Link>
        </Text>
      </Box>
    </Container>
  )
}

export const getServerSideProps: GetServerSideProps = compose(
  (ctx, pageProps, next) =>
    MembershipInvitationFetcher(
      ctx,
      pageProps,
      next,
      new MembershipService(),
      true,
    ),
  (ctx, pageProps, fn) =>
    auth(ctx, pageProps, fn, new AuthService(), true, AuthGuardMode.PARTIAL),
  CanAccessPublicRoutes,
  (ctx, pageProps, fn) => MembershipInvitationRedirecter(ctx, pageProps, fn),
  (ctx, pageProps) => MembershipAcceptedRedirecter(ctx, pageProps),
)

export default Register
